import React, { useMemo, useCallback, useRef, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Visibility } from '@material-ui/icons';
import { format, parseISO, subDays } from 'date-fns';
import { Modal, UnidataTable } from '~/components';
import { isArray } from 'lodash';
import { toast } from 'react-toastify';
import { t } from '~/helpers';
import api from '~/services/api';
import {
  dataInRequest,
  findData,
  recordFiltering,
} from '~/store/modules/recalculaTurno/actions';

import MyForm from './form';
import View from './view';

function RecalculaTurno() {
  const delayRefresh = 1;

  const dispatch = useDispatch();

  const redux = useSelector((state) => state.recalculaTurno);
  const modalRef = useRef(null);

  const [loadingView, setLoadingView] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);

  // Options
  const status = useMemo(
    () => [
      { label: 'S', value: t('s.geral.opcoes.sim') },
      { label: 'N', value: t('s.geral.opcoes.nao') },
    ],
    []
  );

  const buttons = useMemo(
    () => [
      {
        label: t('s.geral.botaofechar'),
        callback: () => modalRef.current.handleCloseModal(),
        color: 'secondary',
        autoFocus: true,
        variant: 'outlined',
      },
    ],
    []
  );

  const handleView = useCallback(async (value) => {
    try {
      setLoadingView(true);
      const { data } = await api.get(
        `recalculaturno/item/${value.idhistoricorecalculaturno}`
      );
      modalRef.current.handleOpenModal({ memoText: data?.linhahistoricorecalculaturnoitem || "Não registrado" });
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : typeof err === 'string'
            ? err
            : 'Erro ao carregar os detalhes.';

      if (isArray(err)) {
        err.forEach((e) => {
          const message =
            e instanceof Error ? e.message : typeof e === 'string' ? e : 'Erro ao carregar os detalhes.';
          toast.error(message);
        });
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setLoadingView(false);
    }
  }, []);

  const actions = useMemo(
    () => [
      {
        title: t('s.geral.visualizar'),
        action: (value) => handleView(value),
        icon: <Visibility />,
      },
    ],
    [handleView]
  );

  // Header tabela
  const header = useMemo(
    () => [
      {
        id: 'datahorahistoricorecalculaturno',
        label: t('s.operacao.historicorecalculaturno.datahorahistoricorecalculaturno'),
        formatted: (value) =>
          value ? format(parseISO(value), 'dd/MM/yyyy HH:mm') : '',
        filter: {
          type: 'initialdatetime',
          defaultValue: format(subDays(new Date(), 7), 'dd/MM/yyyy 00:00:00'),
        },
      },
      {
        id: 'datahorafimexecucaohistoricorecalculaturno',
        label: t('s.operacao.historicorecalculaturno.datahorafimexecucaohistoricorecalculaturno'),
        formatted: (value) =>
          value ? format(parseISO(value), 'dd/MM/yyyy HH:mm') : '',
      },
      {
        id: 'codigonomeposto',
        label: t('s.configuracao.posto.nomeposto'),
        filter: { type: 'text' },
      },
      {
        id: 'strstatusdatahorafimexecucaohistoricorecalculaturno',
        label: t('s.operacao.historicorecalculaturno.strstatusdatahorafimexecucaohistoricorecalculaturno'),
        filter: { type: 'text' },
      },
      {
        id: 'codigousuario',
        label: t('s.cadastro.usuario.codigousuario'),
        filter: { type: 'text' },
      },
      {
        id: 'dataHoraprocessariniciohistoricorecalculaturno',
        label: t('s.operacao.historicorecalculaturno.dataHoraprocessariniciohistoricorecalculaturno'),
        formatted: (value) =>
          value ? format(parseISO(value), 'dd/MM/yyyy') : '',
      },
      {
        id: 'idturno',
        label: t('s.operacao.turno.idturno'),
      },
      {
        id: 'foiturnothistoricorecalculaturno',
        label: t('s.operacao.historicorecalculaturno.foiturnothistoricorecalculaturno'),
        formatted: (value) => status.find((s) => s.label === value).label || '',
        filter: { type: 'select', options: status },
      },
    ],
    [status]
  );

  useEffect(() => {
    const intervalId = setInterval(() => {
      handleGetDispatchRedux(redux?.params);
    }, delayRefresh * 1000 * 60);

    return () => clearInterval(intervalId);
  }, [redux.params]);

  const handleGetDispatchRedux = useCallback((query) => {
    dispatch(recordFiltering(query));
  }, []);


  return (
    <>
      <UnidataTable
        fixed
        redux={redux}
        header={header}
        read={recordFiltering}
        // read={dataInRequest}
        actions={actions}
        find={findData}
        update={false}
        delete={false}
        form={MyForm}
        keys="idhistoricorecalculaturno"
        detach="idhistoricorecalculaturno"
      />

      <Modal
        disableBackdropClick
        disableEscapeKeyDown
        ref={modalRef}
        loading={loadingView}
        component={View}
        actions={buttons}
        title={t('s.menu.configabastek.recalculaturno')}
        maxWidth="md"
        noPadding
      />
    </>
  );
}

export default RecalculaTurno;
